import {
  IMPACT,
  PLAYER,
  POSSESSION,
  SKILL,
  fieldPlayersPerTeam,
  type Ball,
  type Player,
  type Possession,
} from "@golazo/shared";
import { clamp, clampLength, length, len2 } from "../core/math.js";
import { canCaptureLoft } from "../physics/loft.js";
import { onCrossReception, tickCrossFinish } from "./crossFinish.js";

/**
 * Como se ha perdido el balon. Son las cuatro unicas salidas de `updatePossession`
 * cuando habia dueno, y distinguirlas es lo que separa "me lo quitaron" de "se
 * me fue solo": `carry` no tiene rival ninguno detras.
 */
export type PossessionLossKind = "duel" | "take" | "knock" | "carry";

export type OnPossessionLost = (
  kind: PossessionLossKind,
  from: Player,
  by: Player | null,
) => void;

export function createPossession(): Possession {
  return {
    ownerId: null,
    duelWith: null,
    duelTime: 0,
    lockout: new Map(),
    pendingKickKind: null,
    pendingKickSide: null,
    crossFinish: null,
    passSnapshot: null,
    passSnapshotLoose: 0,
    kickoff: null,
  };
}

export function findOwner(players: readonly Player[], possession: Possession): Player | null {
  if (!possession.ownerId) return null;
  return players.find((p) => p.id === possession.ownerId) ?? null;
}

/**
 * Distancia a la que se coloca el balon delante del jugador. Crece con la
 * velocidad: cuanto mas rapido corres, mas largo es el toque y mas expuesto
 * queda el balon. Es el freno natural a conducir a toda velocidad.
 */
function carryDistance(owner: Player, ball: Ball, speedRatio: number): number {
  // El balon se pega SIEMPRE. Antes esto dependia de mantener Shift, y sin
  // mantenerlo el balon se iba a toques por delante — que es lo que hacia
  // sentir la conduccion resbaladiza a quien no conocia el boton.
  const extra = POSSESSION.CARRY_SPEED_EXTRA * POSSESSION.CLOSE_CONTROL_CARRY_SCALE;
  return owner.radius + ball.radius + POSSESSION.CARRY_BASE_PAD + speedRatio * extra;
}


function releaseTo(possession: Possession, playerId: string | null, lockoutFor?: string): void {
  possession.ownerId = playerId;
  possession.duelWith = null;
  possession.duelTime = 0;
  if (lockoutFor) possession.lockout.set(lockoutFor, POSSESSION.RECAPTURE_LOCKOUT);
}

/**
 * Lleva la cuenta del duelo cuerpo a cuerpo. Devuelve el rival que ya ha
 * aguantado encima lo suficiente como para quitarte el balon, si lo hay.
 */
function updateDuel(
  owner: Player,
  players: readonly Player[],
  possession: Possession,
  dt: number,
): Player | null {
  const rival = players.find(
    (p) =>
      p.side !== owner.side &&
      !possession.lockout.has(p.id) &&
      len2(p.pos.x - owner.pos.x, p.pos.y - owner.pos.y) <=
        p.radius + owner.radius + POSSESSION.CAPTURE_PAD,
  );

  if (!rival) {
    possession.duelWith = null;
    possession.duelTime = 0;
    return null;
  }

  // Si cambia el rival que aprieta, el reloj vuelve a empezar.
  if (possession.duelWith !== rival.id) {
    possession.duelWith = rival.id;
    possession.duelTime = 0;
  }
  possession.duelTime += dt;

  let needed =
    POSSESSION.DUEL_TIME * 1;

  // Escudo de cuerpo: de espaldas al rival aguantas mas el duelo.
  const toRivalX = rival.pos.x - owner.pos.x;
  const toRivalY = rival.pos.y - owner.pos.y;
  const toRivalLen = len2(toRivalX, toRivalY) || 1;
  const faceIntoPressure =
    owner.facing.x * (toRivalX / toRivalLen) + owner.facing.y * (toRivalY / toRivalLen);
  if (faceIntoPressure < -0.25) needed *= POSSESSION.SHIELD_DUEL_SCALE;
  // Cambio de ritmo: vas por delante del marcador un instante.
  if (owner.burstTime > 0) needed *= SKILL.BURST_DUEL_SCALE;
  // Rival que acaba de comerse un fake: el duelo se alarga a tu favor.
  if (rival.hesitateTime > 0) needed *= 1.25;

  return possession.duelTime >= needed ? rival : null;
}

/**
 * Posesion suave. Cada tick:
 *  1. caducan los bloqueos de recaptura,
 *  2. si alguien lleva el balon, un rival puede robarselo por contacto,
 *  3. el balon se arrastra hacia su punto de conduccion con correccion limitada,
 *  4. si se aleja mas que la correa, se suelta,
 *  5. si esta suelto, el jugador elegible mas cercano lo captura.
 *
 * Devuelve `true` si el balon esta siendo conducido tras este tick.
 */
export function updatePossession(
  ball: Ball,
  players: readonly Player[],
  possession: Possession,
  dt: number,
  onLost?: OnPossessionLost,
): boolean {
  for (const [id, remaining] of possession.lockout) {
    const next = remaining - dt;
    if (next <= 0) possession.lockout.delete(id);
    else possession.lockout.set(id, next);
  }

  const owner = findOwner(players, possession);
  tickCrossFinish(possession, owner, dt);

  if (owner) {
    // Duelo ganado por insistencia: el balon queda suelto entre los dos.
    const winner = updateDuel(owner, players, possession, dt);
    if (winner) {
      onLost?.("duel", owner, winner);
      releaseTo(possession, null, owner.id);
      const dx = winner.pos.x - owner.pos.x;
      const dy = winner.pos.y - owner.pos.y;
      const len = len2(dx, dy) || 1;
      ball.vel.x = (dx / len) * -POSSESSION.DUEL_KNOCK_SPEED;
      ball.vel.y = (dy / len) * -POSSESSION.DUEL_KNOCK_SPEED;
      // Perder el pulso del duelo tambien desequilibra, igual que una entrada.
      owner.stumbleTime = Math.max(owner.stumbleTime, IMPACT.STUMBLE_TIME);
      return false;
    }

    const disruption = findDisruption(ball, owner, players, possession);

    if (disruption?.kind === "take") {
      onLost?.("take", owner, disruption.by);
      applyFirstTouch(ball, disruption.by, players);
      onCrossReception(
        disruption.by,
        possession,
        players.filter((p) => p.side !== disruption.by.side),
      );
      releaseTo(possession, disruption.by.id, owner.id);
      if (carryBall(ball, disruption.by, dt)) return true;
      releaseTo(possession, null, owner.id);
      return false;
    }

    if (disruption?.kind === "knock") {
      // Entrada al cuerpo con el balon fuera del alcance: no se lo lleva, lo
      // descuelga. Quien entra NO se bloquea, para poder ir a por el rechace.
      onLost?.("knock", owner, disruption.by);
      releaseTo(possession, null, owner.id);
      ball.vel.x += disruption.by.vel.x * POSSESSION.KNOCK_TRANSFER;
      ball.vel.y += disruption.by.vel.y * POSSESSION.KNOCK_TRANSFER;
      // Quien pierde el balon por una entrada fuerte queda un instante
      // descolocado: no solo se queda sin balon, se queda mal parado.
      owner.stumbleTime = Math.max(owner.stumbleTime, IMPACT.STUMBLE_TIME);
      return false;
    }

    if (carryBall(ball, owner, dt)) return true;
    // Si el balon se escapa por el "mini pase" (soltar Shift), no es una
    // perdida de control: es el gesto que la ha causado. No se bloquea al
    // propio jugador para poder correr a por el, igual que en el toque normal.
    onLost?.("carry", owner, null);
    releaseTo(possession, null, owner.releaseGrace > 0 ? undefined : owner.id);
    return false;
  }

  const catcher = findCatcher(ball, players, possession);
  if (catcher) {
    applyFirstTouch(ball, catcher, players);
    onCrossReception(
      catcher,
      possession,
      players.filter((p) => p.side !== catcher.side),
    );
    ball.unstoppable = false;
    releaseTo(possession, catcher.id);
    return carryBall(ball, catcher, dt);
  }

  return false;
}

/**
 * Lleva el balon hacia su punto de conduccion. Devuelve `false` si se ha
 * escapado (mas alla de la correa), en cuyo caso conserva su velocidad y sigue
 * suelto.
 *
 * Clave: no se le *fija* la velocidad al balon, se le empuja hacia la deseada
 * con aceleracion limitada. Asi el balon conserva su inercia y al cortar de
 * golpe se queda atras, que es justo lo que hace que conducir tenga riesgo.
 */
function carryBall(ball: Ball, owner: Player, dt: number): boolean {
  // Al controlarlo se mata el efecto: un balon conducido no comba.
  ball.spin = 0;
  ball.topSpin = 0;
  ball.loft = 0;
  ball.loftVel = 0;

  if (
    len2(owner.pos.x - ball.pos.x, owner.pos.y - ball.pos.y) > POSSESSION.CONTROL_RADIUS
  ) {
    return false;
  }

  const speed = length(owner.vel);

  /*
   * Siempre cosido al pie.
   *
   * Aqui habia una rama de "toques": corriendo y sin Shift, el balon salia
   * golpeado hacia delante y rodaba libre. Le daba textura a la conduccion,
   * pero exigia conocer un boton para que el balon no se te escapara — y quien
   * lo probaba sin saberlo pensaba que el juego iba suelto. El balon se pega
   * solo, y a cambio se pierde ese matiz: es un cambio de publico, no un
   * arreglo.
   */
  return glueBall(ball, owner, speed, dt);
}

/** Balon cosido al pie: muelle hacia el punto de conduccion. */
function glueBall(ball: Ball, owner: Player, speed: number, dt: number): boolean {
  const speedRatio = Math.min(speed / PLAYER.MAX_SPEED, 1);
  const distance = carryDistance(owner, ball, speedRatio);
  const targetX = owner.pos.x + owner.facing.x * distance;
  const targetY = owner.pos.y + owner.facing.y * distance;

  const dx = targetX - ball.pos.x;
  const dy = targetY - ball.pos.y;

  const correction = {
    x: dx * POSSESSION.CORRECTION_STIFFNESS,
    y: dy * POSSESSION.CORRECTION_STIFFNESS,
  };
  clampLength(correction, POSSESSION.CORRECTION_MAX);

  const delta = {
    x: owner.vel.x + correction.x - ball.vel.x,
    y: owner.vel.y + correction.y - ball.vel.y,
  };
  clampLength(delta, POSSESSION.CARRY_ACCEL * dt);

  ball.vel.x += delta.x;
  ball.vel.y += delta.y;
  return true;
}

/**
 * `take`  — el rival se queda el balon, porque llega a el antes que quien lo
 *           conduce. Solo pasa si el toque iba largo: es el precio de correr.
 * `knock` — entrada al cuerpo con velocidad: el balon no queda al alcance del
 *           que entra, asi que sale descolgado y queda suelto para los dos.
 */
type Disruption = { kind: "take" | "knock"; by: Player } | null;

function findDisruption(
  ball: Ball,
  owner: Player,
  players: readonly Player[],
  possession: Possession,
): Disruption {
  const ownerToBall = len2(owner.pos.x - ball.pos.x, owner.pos.y - ball.pos.y);

  // Con el control cerrado el balon esta protegido: hay que llegarle mas cerca
  // y con mas fuerza para quitarselo.
  const resistance = 1;

  for (const player of players) {
    if (player.side === owner.side) continue;
    if (possession.lockout.has(player.id)) continue;

    const toBall = len2(player.pos.x - ball.pos.x, player.pos.y - ball.pos.y);
    const ballReach = player.radius + ball.radius + POSSESSION.INTERCEPT_PAD / resistance;

    // Escudo de cuerpo: si el dueno se ha puesto entre el rival y el balon, no
    // basta con llegar un pelo antes — hay que llegar CLARAMENTE antes.
    //
    // Se actua sobre el margen y no sobre `ballReach` porque el alcance es casi
    // todo radios (11 + 6) y `INTERCEPT_PAD` son 4: dividir esos 4 por la
    // resistencia mueve el alcance un 9%, no el 1,9x que sugiere el nombre de
    // la constante. Ese es el motivo de que el control cerrado no protegiera de
    // nada — medido: el 100% de los robos ocurrian con `closeControl` activo.
    //
    // Mismo criterio geometrico que el escudo del duelo, pero medido contra el
    // BALON en vez de contra `facing`: aqui lo que importa es donde queda el
    // balon, que es lo que el rival intenta alcanzar.
    let margin = 1;
    const toBallX = ball.pos.x - owner.pos.x;
    const toBallY = ball.pos.y - owner.pos.y;
    const toRivalX = player.pos.x - owner.pos.x;
    const toRivalY = player.pos.y - owner.pos.y;
    const lenBall = len2(toBallX, toBallY) || 1;
    const lenRival = len2(toRivalX, toRivalY) || 1;
    const shieldDot =
      (toBallX / lenBall) * (toRivalX / lenRival) + (toBallY / lenBall) * (toRivalY / lenRival);
    if (shieldDot < POSSESSION.SHIELD_TAKE_DOT) margin = POSSESSION.SHIELD_TAKE_MARGIN;

    if (toBall <= ballReach && toBall < ownerToBall * margin) {
      return { kind: "take", by: player };
    }

    const bodyReach = player.radius + owner.radius + POSSESSION.CAPTURE_PAD;
    if (len2(player.pos.x - owner.pos.x, player.pos.y - owner.pos.y) > bodyReach) continue;

    const relSpeed = len2(player.vel.x - owner.vel.x, player.vel.y - owner.vel.y);
    const stealNeed =
      POSSESSION.STEAL_REL_SPEED * (fieldPlayersPerTeam() === 2 ? 0.88 : 1) * resistance;
    if (relSpeed >= stealNeed) return { kind: "knock", by: player };
  }
  return null;
}

/**
 * Primer toque: si el balon llega caliente, se absorbe velocidad y se abre
 * una ventana corta sin golpeo. Controlar es un gesto, no un interruptor.
 *
 * Para un balon de verdad rapido (pase o tiro, no un rebote suelto), recibirlo
 * limpio exige tener Shift pulsado EN ESE INSTANTE. Sin el, apenas se absorbe
 * velocidad: el balon se escapa hacia delante como un control de pecho mal
 * preparado. Es deliberado, no aleatorio — el jugador que anticipa y pulsa
 * Shift antes de que llegue el balon lo controla siempre; el que no, nunca.
 * Por debajo de FIRST_TOUCH_SPEED (balon lento/suelto) da igual: no hace
 * falta prepararse para algo que casi no se mueve.
 */
function applyFirstTouch(ball: Ball, player: Player, players: readonly Player[]): void {
  const relX = ball.vel.x - player.vel.x;
  const relY = ball.vel.y - player.vel.y;
  const relSpeed = len2(relX, relY);
  if (relSpeed < POSSESSION.FIRST_TOUCH_SPEED) {
    player.trapTime = 0;
    return;
  }

  let absorb = player.wantsCloseControl
    ? POSSESSION.FIRST_TOUCH_ABSORB
    : POSSESSION.FIRST_TOUCH_ABSORB_LOOSE;

  // Primer toque imperfecto: balon caliente + presion o de espaldas al envio.
  let pressureDist = Infinity;
  for (const rival of players) {
    if (rival.side === player.side || rival.role === "gk") continue;
    pressureDist = Math.min(
      pressureDist,
      len2(rival.pos.x - player.pos.x, rival.pos.y - player.pos.y),
    );
  }
  const toBallX = ball.pos.x - player.pos.x;
  const toBallY = ball.pos.y - player.pos.y;
  const facingBall = player.facing.x * toBallX + player.facing.y * toBallY > 0;
  const pressured = pressureDist < POSSESSION.FIRST_TOUCH_PRESSURE;
  const hot = relSpeed > POSSESSION.FIRST_TOUCH_SPEED * 1.1;

  if (hot && !player.wantsCloseControl && (pressured || !facingBall)) {
    const speedT = clamp((relSpeed - POSSESSION.FIRST_TOUCH_SPEED) / 380, 0, 1);
    const pressT = clamp(1 - pressureDist / POSSESSION.FIRST_TOUCH_PRESSURE, 0, 1);
    let err =
      POSSESSION.FIRST_TOUCH_ERROR_MAX *
      speedT *
      (0.45 + pressT * 0.55) *
      (facingBall ? 1 : 1.2);
    // Modos abiertos: mas balones sueltos mantienen la circulacion.
    if (players.length <= 6) err *= 1.35;
    absorb *= 1 - err;
    const cross = (relX * player.facing.y - relY * player.facing.x) / Math.max(relSpeed, 1);
    const sign = cross >= 0 ? 1 : -1;
    ball.vel.x += sign * err * relSpeed * 0.06 * player.facing.y;
    ball.vel.y -= sign * err * relSpeed * 0.06 * player.facing.x;
  }

  ball.vel.x = player.vel.x + relX * (1 - absorb);
  ball.vel.y = player.vel.y + relY * (1 - absorb);
  ball.spin *= 0.35;
  if (ball.loft > 0) {
    ball.loftVel *= 0.12;
  }

  // Recepcion orientada solo en lados chicos / cuando el stick abre de verdad.
  // En 11v11 la IA lleva Shift casi siempre y dispersaba cada control.
  const ox = player.steerIntent.x;
  const oy = player.steerIntent.y;
  const oLen = len2(ox, oy);
  if (
    player.wantsCloseControl &&
    oLen > 0.45 &&
    relSpeed >= POSSESSION.FIRST_TOUCH_SPEED &&
    fieldPlayersPerTeam() <= 5
  ) {
    const nx = ox / oLen;
    const ny = oy / oLen;
    const inX = relX / relSpeed;
    const inY = relY / relSpeed;
    const intoPass = nx * inX + ny * inY;
    if (intoPass <= 0.1) {
      const keep = POSSESSION.ORIENT_TRAP_KEEP;
      const push = POSSESSION.ORIENT_TRAP_PUSH * (pressured ? 1.08 : 0.92);
      ball.vel.x = ball.vel.x * keep + nx * push;
      ball.vel.y = ball.vel.y * keep + ny * push;
      player.facing.x = nx;
      player.facing.y = ny;
      player.aimDir.x = nx;
      player.aimDir.y = ny;
    }
  }

  player.trapTime = player.wantsCloseControl ? POSSESSION.TRAP_TIME : POSSESSION.TRAP_TIME * 0.5;
}

/** Jugador elegible mas cercano al balon suelto. */
function findCatcher(
  ball: Ball,
  players: readonly Player[],
  possession: Possession,
): Player | null {
  let best: Player | null = null;
  let bestDist = Infinity;

  for (const player of players) {
    if (possession.lockout.has(player.id)) continue;
    if (!canCaptureLoft(ball)) continue;

    const reach = player.radius + ball.radius + POSSESSION.CAPTURE_PAD;
    const dist = len2(player.pos.x - ball.pos.x, player.pos.y - ball.pos.y);
    if (dist <= reach && dist < bestDist) {
      best = player;
      bestDist = dist;
    }
  }

  return best;
}

export type { KickKind, KickContext, BackNutmegResult } from "./kick.js";

export {
  kickBall,
  chargeCurve,
  shouldCross,
  isInCrossingZone,
  defaultCrossTarget,
  applyFake,
  applyCut,
  applyNutmeg,
  applyBackNutmeg,
  isLateralCut,
  findNutmegTarget,
  findRearSkillTarget,
} from "./kick.js";
